import { useEffect, useMemo, useState } from 'react';
import { OrderBy } from '../../../../../components/Hooks/useList.types';
import useInfiniteDataQuery from '../../../../../hooks/useInfiniteDataQuery/useInfiniteDataQuery';
import { InfiniteDataQueryFnUtils } from '../../../../../hooks/useInfiniteDataQuery/utils/useInfiniteDataQueryFn.utils';
import type { PaginatedApiFetcher } from '../../../../../hooks/useInfiniteDataQuery/utils/useInfiniteDataQueryFn.utils';

interface UseCatalogBatchAddEntityModalParams<T extends { id: string }> {
  queryKeyBase: string;
  fetchFn: PaginatedApiFetcher<T>;
  searchParam: string;
  defaultOrderBy: OrderBy;
  isOpen: boolean;
}

const useCatalogBatchAddEntityModal = <T extends { id: string }>({
  queryKeyBase,
  fetchFn,
  searchParam,
  defaultOrderBy,
  isOpen,
}: UseCatalogBatchAddEntityModalParams<T>) => {
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  const searchParams = useMemo(
    () => (search ? { [searchParam]: search } : {}),
    [searchParam, search],
  );

  const infiniteQuery = useInfiniteDataQuery<T>({
    limit: 20,
    queryKey: [queryKeyBase, searchParams],
    defaultOrderBy,
    queryFn: InfiniteDataQueryFnUtils.fetcherWithCountAndParams(fetchFn, searchParams),
    isEnabled: isOpen,
  });

  const items = infiniteQuery.query.pagesData ?? [];

  return {
    search,
    setSearch,
    items,
    hasNextPage: infiniteQuery.query.hasNextPage ?? false,
    fetchNextPage: infiniteQuery.query.fetchNextPage,
    isLoading: infiniteQuery.query.isLoading,
  };
};

export default useCatalogBatchAddEntityModal;
